import { StringContext } from "../../../core/StringContext";
import { StringDevModule, StringDevOverlayConfig } from "./StringDevModule";
import { StringObject } from "../../../objects/StringObject";
import { styleTxn } from "../../../utils/style-txn";
import {
  emitDevtoolsOverlayLayoutChange,
  registerActiveOverlay,
  resolveDevtoolsCollisionOffset,
  resolveDevtoolsOverlayAnchor,
  STRING_DEVTOOLS_OVERLAY_LAYOUT_CHANGE_EVENT,
  StringDevtoolsOverlayId,
  unregisterActiveOverlay,
} from "./StringDevtoolsOverlayLayout";
import { StringDevViewportLayer } from "./StringDevViewportLayer";

export interface OverlayBaseMetrics {
  visible: boolean;
  contentX: number;
  contentY: number;
  width: number;
  height: number;
  isOffscreen: boolean;
  baseAnchorX: number;
  baseAnchorY: number;
  collisionOffset: number;
}

export interface StringDevOverlayBadgeInit {
  targetId: string;
  selectorAttribute?: string;
  depth?: number;
  attributes?: Record<string, string | boolean | number | null | undefined>;
}

export interface StringDevOverlayBadgeState {
  visible?: boolean;
  active?: boolean;
  disabled?: boolean;
  title?: string;
  html?: string;
  attributes?: Record<string, string | boolean | number | null | undefined>;
}

export abstract class StringDevOverlayModule<
  TEntry = unknown,
  TMeasurement = unknown,
> extends StringDevModule {
  private _viewportLayer?: StringDevViewportLayer;
  protected get viewportLayer(): StringDevViewportLayer {
    if (!this._viewportLayer) {
      this._viewportLayer = this.acquireViewportLayer(this.layerName, this.layerZIndex);
    }
    return this._viewportLayer;
  }
  protected readonly entries = new Map<string, TEntry>();
  protected readonly measurements = new Map<string, TMeasurement>();

  protected enabled = false;
  protected needsMeasure = false;
  protected hasPendingMutate = false;

  private readonly onOverlayLayoutChangeBind = () => {
    if (!this.enabled) return;
    this.scheduleFullSync();
  };

  private get _overlayConfig(): StringDevOverlayConfig | undefined {
    return (this.constructor as typeof StringDevModule).devtool?.overlay;
  }

  /** Override to provide the overlay ID for the anchor/collision system. */
  protected get overlayId(): StringDevtoolsOverlayId | null {
    return this._overlayConfig?.overlayId ?? null;
  }

  /** Override to define your viewport layer name. Falls back to devtool.overlay.layerName. */
  protected get layerName(): string {
    return this._overlayConfig?.layerName ?? "";
  }

  /** Override to define your viewport layer z-index. Falls back to devtool.overlay.zIndex. */
  protected get layerZIndex(): number {
    return this._overlayConfig?.zIndex ?? 10011;
  }

  /** Override to define the data-attribute added to the layer root. Falls back to devtool.overlay.layerAttribute. */
  protected get layerAttribute(): string {
    return this._overlayConfig?.layerAttribute ?? "";
  }

  /** Override when an overlay module should start enabled before dock preferences apply. Falls back to devtool.overlay.defaultEnabled. */
  protected get defaultEnabled(): boolean {
    return this._overlayConfig?.defaultEnabled ?? false;
  }

  constructor(context: StringContext) {
    super(context);
    this.enabled = this.defaultEnabled;
  }

  onInit(): void {
    this.ensureLayer();
    this.applyEnabledState();
    window.addEventListener(
      STRING_DEVTOOLS_OVERLAY_LAYOUT_CHANGE_EVENT,
      this.onOverlayLayoutChangeBind,
    );
  }

  onObjectConnected(object: StringObject): void {
    this.ensureLayer();
    const entry = this.createOverlayEntry(object);
    if (!entry) return;

    this.entries.set(object.id, entry);
    this.scheduleFullSync();
  }

  onObjectDisconnected(object: StringObject): void {
    const entry = this.entries.get(object.id);
    if (!entry) return;

    this.destroyOverlayEntry(entry);
    this.entries.delete(object.id);
    this.measurements.delete(object.id);
  }

  public setEnabled(enabled: boolean): void {
    if (this.enabled === enabled) return;
    this.enabled = enabled;
    
    this.applyEnabledState();
    this.onEnabledChange(enabled);
    this.scheduleFullSync();
    this.emitDevtoolState();
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  protected getDevtoolActiveState(): boolean {
    return this.enabled;
  }

  protected setDevtoolActiveState(active: boolean): void {
    this.setEnabled(active);
  }

  // --- Abstract Methods ---

  /** Create DOM and return state for the given element */
  protected abstract createOverlayEntry(object: StringObject): TEntry | null;

  /** Clean up DOM and listeners for the given element */
  protected abstract destroyOverlayEntry(entry: TEntry): void;

  /** Produce a custom measurement state utilizing the provided base metrics */
  protected abstract measureEntry(entry: TEntry, baseMetrics: OverlayBaseMetrics): TMeasurement;

  /** Render DOM efficiently utilizing `styleTxn` */
  protected abstract applyMeasurement(entry: TEntry, measurement: TMeasurement | undefined): void;

  /** Called when `setEnabled` state flips, allowing subclasses to show/hide specific entries */
  protected onEnabledChange(_enabled: boolean): void {}

  // --- Render Loop (Measurement and Syncing) ---

  onScroll(): void {
    if (!this.enabled) return;
    this.needsMeasure = true;
  }

  onScrollMeasure(): void {
    if (!this.enabled || !this.needsMeasure) return;
    this.collectMeasurements();
    this.needsMeasure = false;
    this.flushMeasurements();
    this.hasPendingMutate = false;
  }

  onMutate(): void {
    if (!this.enabled || !this.hasPendingMutate) return;
    this.flushMeasurements();
    this.hasPendingMutate = false;
  }

  onResize(): void {
    if (!this.enabled) return;
    this.scheduleFullSync();
  }

  onDOMRebuild(): void {
    if (!this.enabled) return;
    this.scheduleFullSync();
  }

  // --- Internal implementations ---

  protected scheduleFullSync(): void {
    this.ensureLayer();
    this.needsMeasure = true;
    this.collectMeasurements();
    this.needsMeasure = false;
    this.flushMeasurements();
    this.hasPendingMutate = false;
  }

  protected collectMeasurements(): void {
    this.ensureLayer();
    for (const [id, entry] of this.entries) {
      const bMetrics = this.computeBaseMetrics(entry);
      this.measurements.set(id, this.measureEntry(entry, bMetrics));
    }
  }

  protected flushMeasurements(): void {
    styleTxn.run(() => {
      for (const [id, entry] of this.entries) {
        this.applyMeasurement(entry, this.measurements.get(id));
      }
    });
  }

  protected ensureLayer(): HTMLDivElement {
    const root = this.viewportLayer.ensure(this.getWorldHost());
    if (this.layerAttribute) {
      root.setAttribute(this.layerAttribute, "");
    }
    return root;
  }

  protected applyEnabledState(): void {
    const layer = this.viewportLayer.getElement();
    if (!layer) return;

    layer.setAttribute("data-enabled", this.enabled ? "true" : "false");

    if (this.layerAttribute) {
      if (this.enabled) {
        layer.setAttribute(this.layerAttribute, "");
      } else {
        layer.removeAttribute(this.layerAttribute);
      }
    }
    
    // Register / unregister with in-memory overlay registry
    if (this.overlayId) {
      if (this.enabled) {
        registerActiveOverlay(this.overlayId, this.devtoolConfig?.order ?? 0);
      } else {
        unregisterActiveOverlay(this.overlayId);
      }
    }
    
    // We emit layout changes so other devtools can recompute collisions if we enabled/disabled
    emitDevtoolsOverlayLayoutChange();
  }

  protected resolveOverlayTargetId(object: StringObject): string {
    return object.htmlElement.id || object.id;
  }

  protected resolveOverlayObjectDepth(element: HTMLElement): number {
    let depth = 0;
    let current = element.parentElement;

    while (current) {
      if (current.hasAttribute("string-id") || current.hasAttribute("data-string-id")) {
        depth += 1;
      }
      current = current.parentElement;
    }

    return depth;
  }

  protected createOverlayBadge(init: StringDevOverlayBadgeInit): HTMLButtonElement {
    const badge = document.createElement("button");
    badge.type = "button";

    badge.setAttribute("data-string-devtools-overlay-badge", "");
    badge.setAttribute("data-target-id", init.targetId);

    if (init.selectorAttribute) {
      badge.setAttribute(init.selectorAttribute, "");
    }

    if (init.depth != null) {
      badge.setAttribute("data-depth", String(init.depth));
    }

    this.applyOverlayBadgeState(badge, { attributes: init.attributes });
    return badge;
  }

  protected applyOverlayBadgeState(
    badge: HTMLElement,
    state: StringDevOverlayBadgeState,
  ): void {
    if (state.visible != null) {
      badge.setAttribute("data-visible", state.visible ? "true" : "false");
    }

    if (state.active != null) {
      badge.setAttribute("data-active", state.active ? "true" : "false");
    }

    if (state.disabled != null && badge instanceof HTMLButtonElement) {
      badge.disabled = state.disabled;
    }

    if (state.title != null) {
      badge.title = state.title;
    }

    if (state.html != null) {
      badge.innerHTML = state.html;
    }

    if (!state.attributes) return;

    for (const [key, value] of Object.entries(state.attributes)) {
      if (value == null) {
        badge.removeAttribute(key);
        continue;
      }

      if (typeof value === "boolean") {
        badge.setAttribute(key, value ? "true" : "false");
        continue;
      }

      badge.setAttribute(key, String(value));
    }
  }

  protected applyOverlayBadgePosition(
    badge: HTMLElement,
    measurement: { visible: boolean; docX: number; docY: number } | undefined,
  ): void {
    if (!measurement) {
      badge.setAttribute("data-visible", "false");
      return;
    }

    badge.setAttribute("data-visible", measurement.visible ? "true" : "false");
    if (!measurement.visible) {
      return;
    }

    styleTxn.setProps(badge, {
      transform: `translate3d(${Math.round(measurement.docX * 100) / 100}px, ${Math.round(measurement.docY * 100) / 100}px, 0)`,
    });
  }

  protected computeBaseMetrics(entry: any): OverlayBaseMetrics {
    const object = entry.object as StringObject;
    const element = object.htmlElement;
    if (!this.enabled || !element || !element.isConnected) {
      return this.getHiddenMetrics();
    }

    const width =
      (object.getProperty<number>("layout-width") ??
      ((object.getProperty<number>("half-width") ?? 0) * 2)) ||
      element.offsetWidth ||
      element.clientWidth ||
      0;

    const measuredSize = object.getProperty<number>("size");
    const layoutHeight =
      (object.getProperty<number>("layout-height") ??
      ((object.getProperty<number>("half-height") ?? 0) * 2)) ||
      element.offsetHeight ||
      element.clientHeight ||
      0;
      
    const height = measuredSize != null && measuredSize > 0 ? measuredSize : layoutHeight;
    const contentX = object.getProperty<number>("layout-doc-left") ?? 0;
    const contentY = this.getObjectDocY(object);

    if (width <= 0 || height <= 0) {
      return this.getHiddenMetrics();
    }

    const viewportHeight = this.data.viewport.windowHeight || window.innerHeight;
    const viewportWidth = this.data.viewport.windowWidth || window.innerWidth;
    const scrollLeft = this.getViewportScrollLeft();
    const scrollTop = this.getViewportScrollTop();
    
    const viewportLeft = contentX - scrollLeft;
    const viewportTop = contentY - scrollTop;
    
    const isOffscreen =
      viewportTop + height < 0 ||
      viewportTop > viewportHeight ||
      viewportLeft + width < 0 ||
      viewportLeft > viewportWidth;

    if (isOffscreen) {
      return this.getHiddenMetrics();
    }

    let baseAnchorX = contentX;
    let baseAnchorY = contentY;
    let collisionOffset = 0;

    if (this.overlayId) {
      const anchor = resolveDevtoolsOverlayAnchor(this.overlayId, contentX, contentY);
      baseAnchorX = anchor.docX;
      baseAnchorY = anchor.docY;
      
      collisionOffset = resolveDevtoolsCollisionOffset(
        element,
        baseAnchorX,
        baseAnchorY,
        (ancestorId) => {
          const ancestorObject = this.objectManager.all.get(ancestorId);
          if (!ancestorObject) return null;
          return resolveDevtoolsOverlayAnchor(
            this.overlayId!,
            ancestorObject.getProperty<number>("layout-doc-left") ?? 0,
            this.getObjectDocY(ancestorObject),
          );
        },
      );
    }

    return {
      visible: true,
      contentX,
      contentY,
      width,
      height,
      isOffscreen,
      baseAnchorX,
      baseAnchorY,
      collisionOffset,
    };
  }

  private getHiddenMetrics(): OverlayBaseMetrics {
    return {
      visible: false,
      contentX: 0,
      contentY: 0,
      width: 0,
      height: 0,
      isOffscreen: true,
      baseAnchorX: 0,
      baseAnchorY: 0,
      collisionOffset: 0,
    };
  }

  protected getObjectDocY(object: StringObject): number {
    const start = object.getProperty<number>("start");
    const size = object.getProperty<number>("size");
    if (start != null && size != null && size > 0) {
      return start;
    }
    return object.getProperty<number>("layout-doc-top") ?? 0;
  }

  protected getViewportScrollLeft(): number {
    return this.data.scroll.elementContainer?.scrollLeft ?? 0;
  }

  protected getViewportScrollTop(): number {
    return this.data.scroll.elementContainer?.scrollTop ?? 0;
  }

  destroy(): void {
    if (this.overlayId) {
      unregisterActiveOverlay(this.overlayId);
    }
    window.removeEventListener(
      STRING_DEVTOOLS_OVERLAY_LAYOUT_CHANGE_EVENT,
      this.onOverlayLayoutChangeBind,
    );
    for (const entry of this.entries.values()) {
      this.destroyOverlayEntry(entry);
    }
    this.entries.clear();
    this.measurements.clear();
    super.destroy();
  }
}
